/**
 * This account's own recording policy.
 *
 * On the account page, not under Settings. Settings is the ORGANISATION — what
 * the product does to every meeting in the tenant — and a personal preference
 * sitting among those cards asks the reader to check a label to work out whose
 * setting they are changing. The avatar is the person; this belongs behind it.
 *
 * It sits under the organisation's policy, and the two combine: the default is
 * **follow the organisation**, and the three overrides apply to this person's
 * meetings only. That is why the pane always states what is actually happening
 * rather than only what was chosen — "Follow the organisation" is a word with
 * no consequence attached unless it says what it currently resolves to.
 *
 * Saved on change rather than behind the page's save bar. That bar belongs to
 * the organisation draft, which is one document written whole; this is one
 * value on one account, and folding it into that draft would mean a personal
 * preference could not be changed by someone without permission to save the
 * organisation's.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { Select } from '@/components/ui/controls'
import { Field } from '@/components/ui/field'
import {
  getPersonalRecording,
  setPersonalRecording,
  type PersonalRecordingMode,
} from '@/data/capture'
import { currentUserId } from '@/lib/api'
import { Consequence, SettingsCard } from '@/features/settings/SettingsCard'

const POLICY_LABEL: Record<string, string> = {
  none: 'nothing is recorded automatically',
  internal: 'meetings where everyone is in the organisation are recorded',
  all: 'every eligible meeting is recorded, including ones with external attendees',
}

export function RecordingPreferences() {
  const queryClient = useQueryClient()
  const userId = currentUserId()

  const personal = useQuery({
    queryKey: ['personal-recording', userId],
    queryFn: () => getPersonalRecording(userId),
  })

  const save = useMutation({
    mutationFn: (mode: PersonalRecordingMode) => setPersonalRecording(userId, mode),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['personal-recording', userId] }),
  })

  const options = [
    {
      value: 'inherit',
      label: 'Follow the organisation',
      description: personal.data
        ? `Currently ${POLICY_LABEL[personal.data.orgDefault]}.`
        : 'Whatever the organisation has set.',
    },
    { value: 'none', label: 'Record nothing of mine', description: POLICY_LABEL.none },
    {
      value: 'internal',
      label: 'Record only my internal meetings',
      description: POLICY_LABEL.internal,
    },
    { value: 'all', label: 'Record everything of mine', description: POLICY_LABEL.all },
  ]

  const chosen = options.find((option) => option.value === personal.data?.mode)

  return (
    <SettingsCard title="Recording" meta="what happens to your meetings">
      {personal.isLoading ? (
        <div className="flex items-center gap-2 py-2 text-xs text-[var(--color-fg-muted)]">
          <Loader2 className="size-3 animate-spin" />
          Checking…
        </div>
      ) : (
        <>
          <Field label="For meetings I organise or attend">
            {(props) => (
              <Select
                id={props.id}
                value={personal.data?.mode ?? 'inherit'}
                onValueChange={(value) => save.mutate(value as PersonalRecordingMode)}
                options={options}
                className="sm:max-w-sm"
              />
            )}
          </Field>

          {/* What is true now, not what was picked — the two differ whenever
              "follow the organisation" is selected. */}
          <Consequence>
            {chosen?.description} A choice made on a single meeting still overrides this.
          </Consequence>
        </>
      )}
    </SettingsCard>
  )
}
